import { DialogCloseEvent } from '@theme/dialog';

/** @type {Promise<any> | undefined} */
let hlsLibraryPromise;

/**
 * Loads hls.js once, only for browsers without native HLS (Safari/iOS play HLS natively).
 * @param {string} url
 * @returns {Promise<any>}
 */
function loadHlsLibrary(url) {
  hlsLibraryPromise ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.onload = () => resolve(/** @type {any} */ (window).Hls);
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return hlsLibraryPromise;
}

/**
 * Product video with adaptive streaming (HLS) and native controls.
 *
 * Shopify's HLS stream switches between 480p/720p/1080p to match the connection, so the picture
 * keeps up with the sound on slow mobile networks. Falls back to the best MP4 when HLS isn't usable.
 *
 * Exposes the same `isPlaying` / `pauseMedia()` / `playWithSound()` API as `deferred-media`, which the
 * zoom dialog uses to start and stop videos as the shopper moves between slides.
 */
class ProductVideo extends HTMLElement {
  isPlaying = false;

  /** @type {any} */
  #hls = null;

  /** @type {Promise<void> | null} */
  #sourceReady = null;

  #controller = new AbortController();

  get video() {
    return /** @type {HTMLVideoElement} */ (this.querySelector('video'));
  }

  connectedCallback() {
    const { signal } = this.#controller;
    const { video } = this;

    video.addEventListener('playing', () => this.#setState(true, false), { signal });
    video.addEventListener('pause', () => this.#setState(false, false), { signal });
    video.addEventListener('ended', () => this.#setState(false, false), { signal });
    video.addEventListener('waiting', () => this.classList.add('is-buffering'), { signal });

    window.addEventListener(DialogCloseEvent.eventName, () => this.pauseMedia(), { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#hls?.destroy();
    this.#hls = null;
  }

  /**
   * @param {boolean} playing
   * @param {boolean} buffering
   */
  #setState(playing, buffering) {
    this.isPlaying = playing;
    this.classList.toggle('is-playing', playing);
    this.classList.toggle('is-buffering', buffering);
  }

  /**
   * Attaches the stream the first time the video is needed, so closed slides cost no bandwidth.
   * @returns {Promise<void>}
   */
  #attachSource() {
    this.#sourceReady ??= this.#chooseSource();
    return this.#sourceReady;
  }

  async #chooseSource() {
    const { video } = this;
    const { hls: hlsUrl, mp4: mp4Url, hlsLibrary } = this.dataset;

    if (hlsUrl && video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = hlsUrl;
      return;
    }

    if (hlsUrl && hlsLibrary && ('MediaSource' in window || 'ManagedMediaSource' in window)) {
      try {
        const Hls = await loadHlsLibrary(hlsLibrary);

        if (Hls?.isSupported()) {
          const hls = new Hls({ capLevelToPlayerSize: true, startLevel: -1 });
          hls.on(Hls.Events.ERROR, (/** @type {string} */ _event, /** @type {any} */ data) => {
            if (!data.fatal) return;
            // Give up on streaming and fall back to the progressive MP4
            hls.destroy();
            this.#hls = null;
            if (mp4Url) {
              video.src = mp4Url;
              video.play().catch(() => {});
            }
          });
          hls.loadSource(hlsUrl);
          hls.attachMedia(video);
          this.#hls = hls;
          return;
        }
      } catch {
        // Library failed to load; use the MP4 below
      }
    }

    if (mp4Url) video.src = mp4Url;
  }

  /**
   * Starts playback with sound (the shopper tapped or swiped here). If the browser blocks audio,
   * plays muted instead; the native controls let them unmute.
   */
  async playWithSound() {
    const { video } = this;
    this.classList.add('is-buffering');
    await this.#attachSource();

    video.muted = false;
    try {
      await video.play();
    } catch {
      video.muted = true;
      await video.play().catch(() => this.classList.remove('is-buffering'));
    }
  }

  pauseMedia() {
    if (!this.#sourceReady) return;
    this.video.pause();
  }
}

if (!customElements.get('product-video')) {
  customElements.define('product-video', ProductVideo);
}
